import { useIdioma, type Traducir } from '../../i18n/idioma';
import React, { useEffect, useRef, useState } from 'react';
import type { Photo } from './media';
import { loadGsap } from './motion';

/* ═══════════════════════════════════════════════════════════════════════════
   Revelado fluido (WebGL)
   ═══════════════════════════════════════════════════════════════════════════

   La fotografía no aparece: se DERRAMA. Un campo de distancia con ruido
   angular abre burbujas orgánicas desde el centro hasta llenar el encuadre.
   El shader es el que aportó el equipo (Originkit «Fluid Image Reveal»), tal
   cual; lo que se reescribió es todo lo que lo rodea, porque el original venía
   preparado para Next.js y aquí habría hecho tres cosas mal.

   QUÉ SE CAMBIÓ Y POR QUÉ — importa, porque son decisiones que se pierden si
   alguien vuelve a pegar el original encima:

   1. **La <img> NO se sustituye por un <canvas>.** El original renderiza solo
      el lienzo: sin texto alternativo, sin imagen para quien navega con
      lector de pantalla, sin nada si WebGL falla y sin nada para el buscador.
      Aquí la <img> real sigue en el marcado con su `alt` y su `srcset`; el
      lienzo se pone ENCIMA y solo toma el relevo cuando ya está todo listo.
   2. **Ni bucle de scroll propio ni `setInterval` de zoom.** El original
      monta su propio `scroll` y un temporizador cada 250 ms. Esta página ya
      tiene Lenis + ScrollTrigger; dos sistemas midiendo lo mismo se pelean y
      el paralaje tiembla. Aquí se usa `IntersectionObserver`, que no cuesta
      nada cuando no pasa nada.
   3. **three.js se pide en diferido y solo si hace falta.** Son ~150 KB
      comprimidos. Un contratista abriendo esto con datos móviles no los paga
      hasta que la foto se acerca a la pantalla — y no los paga NUNCA si pidió
      movimiento reducido, si su equipo no tiene WebGL o si el sistema pide
      ahorro de datos.

   Y dos afinados de rendimiento sobre el original: el plano pasa de 128×128
   segmentos a 1 (el vertex shader no desplaza nada, esos 16.000 vértices no
   pintaban nada) y la cámara pasa de perspectiva a ortográfica, que mapea el
   plano a píxeles sin trigonometría.
   ═══════════════════════════════════════════════════════════════════════════ */

const vertexShader = `
varying vec2 vUv;

void main(){
    vec4 modelPosition = modelMatrix * vec4(position, 1.0);
    vec4 viewPosition = viewMatrix * modelPosition;
    vec4 clipPosition = projectionMatrix * viewPosition;
    gl_Position = clipPosition;
    vUv = uv;
}
`;

const fragmentShader = `
varying vec2 vUv;
uniform float uProgress;      // Avance de la animación [0..1]
uniform vec2 uSize;           // Tamaño del contenedor en píxeles
uniform vec2 uImageSize;      // Dimensiones de la imagen, para la proporción
uniform sampler2D uTexture;
uniform int uBlobCount;
uniform float uFitCover;      // 1 = cover (llena y recorta), 0 = contain
#define PI 3.1415926538
#define TWO_PI 6.28318530718

// Ruido ondulado según el ángulo: es lo que da el borde orgánico a la burbuja
float noise(vec2 point) {
    float frequency = 1.0;
    float angle = atan(point.y, point.x) + uProgress * PI;

    float w0 = (cos(angle * frequency) + 1.0) / 2.0;
    float w1 = (sin(2.0 * angle * frequency) + 1.0) / 2.0;
    float w2 = (cos(3.0 * angle * frequency) + 1.0) / 2.0;
    return (w0 + w1 + w2) / 3.0;
}

float softMax(float a, float b, float k) {
    return log(exp(k * a) + exp(k * b)) / k;
}

// Mínimo suave: funde dos formas en vez de recortarlas
float softMin(float a, float b, float k) {
    return -softMax(-a, -b, k);
}

float circleSDF(vec2 pos, float rad) {
    float a = sin(uProgress * 0.2) * 0.25;
    float amt = 0.5 + a;
    float circle = length(pos);
    circle += noise(pos) * rad * amt;
    return circle;
}

void main() {
    vec4 bg = vec4(0.0, 0.0, 0.0, 0.0);

    // Encaje de la textura: cover recorta por el eje largo, contain deja aire.
    vec2 coverUV = vUv;
    if (uSize.x > 0.0 && uSize.y > 0.0 && uImageSize.x > 0.0 && uImageSize.y > 0.0) {
        float containerAspect = uSize.x / uSize.y;
        float imageAspect = uImageSize.x / uImageSize.y;

        vec2 scale = vec2(1.0);
        if (uFitCover > 0.5) {
            if (containerAspect > imageAspect) scale.y = imageAspect / containerAspect;
            else scale.x = containerAspect / imageAspect;
        } else {
            if (containerAspect > imageAspect) scale.x = containerAspect / imageAspect;
            else scale.y = imageAspect / containerAspect;
        }

        coverUV = (vUv - 0.5) * scale + 0.5;
    }

    vec4 texel = texture2D(uTexture, coverUV);
    if (uFitCover < 0.5 &&
        (coverUV.x < 0.0 || coverUV.x > 1.0 || coverUV.y < 0.0 || coverUV.y > 1.0)) {
        texel = vec4(0.0);
    }
    vec2 coords = vUv * uSize;
    vec2 center = vec2(0.5) * uSize;

    float t = pow(uProgress, 2.5);
    // La diagonal garantiza que la burbuja acabe cubriendo el rectángulo entero
    float maxDim = sqrt(uSize.x * uSize.x + uSize.y * uSize.y);
    float rad = t * maxDim * 1.0;

    float c1 = circleSDF(coords - center, rad);
    float k = 50.0 / max(uSize.x, uSize.y);
    float circle = c1;

    int extraBlobs = uBlobCount - 1;
    for (int i = 0; i < 20; i++) {
        if (i >= extraBlobs) break;

        float idx = float(i);
        float total = float(extraBlobs);

        float baseAngle = idx * TWO_PI / max(total, 1.0);
        float jitter = fract(sin(idx * 127.1 + 311.7) * 43758.5453) * 0.5 - 0.25;
        float angle = baseAngle + jitter;

        float distRatio = 0.25 + 0.2 * fract(sin(idx * 43.3) * 12345.6);
        vec2 offset = vec2(cos(angle), sin(angle)) * distRatio * min(uSize.x, uSize.y);

        float blobDist = length(coords - center - offset);
        float blobNoise = noise(coords - center - offset) * rad * 0.4;
        float blob = blobDist + blobNoise;

        circle = softMin(circle, blob, k);
    }

    circle = step(circle, rad);

    gl_FragColor = mix(bg, texel, circle);
}
`;

interface ReveladoFluidoProps {
    foto: Photo;
    /** Clases de la <img>: proporción, ajuste, etc. */
    className?: string;
    sizes?: string;
    /** Burbujas del revelado. 1 = una sola mancha central. */
    burbujas?: number;
    /**
     * Segundos que tarda en llenarse. 3,4 s es deliberadamente lento: a 2 s el
     * barrido pasaba antes de que te diera tiempo a mirarlo, y el equipo lo
     * reportó como «casi no la puedo ver». Un revelado que no se ve no es un
     * revelado, es un parpadeo.
     */
    duracion?: number;
}

/**
 * El equipo puede no tener WebGL, o el sistema puede pedir ahorro de datos.
 *
 * OJO: aquí NO se consulta `prefers-reduced-motion`, y es deliberado. Este
 * efecto no desplaza nada: la fotografía se revela EN SU SITIO, sin moverse ni
 * escalarse. Es un disolvente con bordes orgánicos — exactamente el tipo de
 * recurso que las pautas recomiendan COMO SUSTITUTO del movimiento, no como
 * algo a suprimir. Y tampoco se acorta: el objetivo es justamente PODER VERLO.
 */
function puedeAnimar(): boolean {
    if (typeof window === 'undefined') return false;

    /* En el teléfono no se monta. Son ~190 KB de three.js más un contexto
       WebGL y un shader, sobre un aparato que además tiene que decodificar el
       vídeo del hero: medido en gama media, el 13,8 % de los cuadros bajaban
       de 30 fps al hacer scroll. La fotografía se ve igual, solo sin barrido.
       Un efecto decorativo no vale un scroll que se traba. */
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return false;

    const conexion = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
    if (conexion?.saveData) return false;

    try {
        const lienzo = document.createElement('canvas');
        return Boolean(lienzo.getContext('webgl2') || lienzo.getContext('webgl'));
    } catch {
        return false;
    }
}

const ReveladoFluido: React.FC<ReveladoFluidoProps> = ({
    foto,
    className = '',
    sizes = '100vw',
    burbujas = 20,
    duracion = 3.4,
}) => {
    const { t } = useIdioma();
    const marcoRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const lienzoRef = useRef<HTMLCanvasElement>(null);
    /** El lienzo ya manda: la <img> se aparta. Hasta entonces, ella es la foto. */
    const [alMando, setAlMando] = useState(false);

    useEffect(() => {
        const marco = marcoRef.current;
        const img = imgRef.current;
        const lienzo = lienzoRef.current;
        if (!marco || !img || !lienzo) return;
        if (!puedeAnimar()) return;

        let vivo = true;
        let soltar: (() => void) | undefined;

        /* Se prepara con 1200 px de antelación, no con 500.
           Con 500 había un agujero real: bastaba bajar rápido con la rueda o
           saltar por un ancla para que la foto pasara de «muy abajo» a «en
           pantalla» SIN cruzar nunca la zona de preparación, y el efecto no
           corría jamás. 1200 px cubre un salto de rueda normal, y para lo que
           no cubre está la salvaguarda de abajo. */
        const vigia = new IntersectionObserver(
            (entradas) => {
                if (!entradas[0].isIntersecting) return;
                vigia.disconnect();
                void preparar();
            },
            { rootMargin: '1200px 0px' },
        );
        vigia.observe(marco);

        const yaSeVe = () => {
            const r = marco.getBoundingClientRect();
            return r.top < window.innerHeight && r.bottom > 0;
        };

        async function preparar() {
            const [THREE, { gsap }] = await Promise.all([import('three'), loadGsap()]);
            if (!vivo || !marcoRef.current) return;

            const textura = await new Promise<InstanceType<typeof THREE.Texture> | null>(
                (resolver) => {
                    new THREE.TextureLoader().load(
                        // `currentSrc` es la variante del srcset que el navegador YA
                        // descargó: la textura sale de la caché, no de una segunda
                        // petición de red.
                        img!.currentSrc || img!.src,
                        (t) => resolver(t),
                        undefined,
                        () => resolver(null),
                    );
                },
            );
            if (!vivo || !textura) return;
            // Sin esto la foto sale lavada: three interpreta la textura como
            // lineal y el resultado pierde contraste y saturación.
            textura.colorSpace = THREE.SRGBColorSpace;

            const escena = new THREE.Scene();
            const camara = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
            camara.position.z = 1;

            const render = new THREE.WebGLRenderer({
                canvas: lienzo!,
                alpha: true,
                antialias: false,
            });
            render.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

            const material = new THREE.ShaderMaterial({
                vertexShader,
                fragmentShader,
                transparent: true,
                uniforms: {
                    uProgress: { value: 0 },
                    uSize: { value: new THREE.Vector2(1, 1) },
                    uImageSize: {
                        // `Texture.image` viene sin tipar en three; aquí siempre
                        // es el HTMLImageElement que acaba de cargar el loader.
                        value: (() => {
                            const bitmap = textura.image as { width?: number; height?: number } | undefined;
                            return new THREE.Vector2(bitmap?.width || 1, bitmap?.height || 1);
                        })(),
                    },
                    uTexture: { value: textura },
                    uBlobCount: { value: Math.min(20, Math.max(1, Math.round(burbujas))) },
                    uFitCover: { value: 1 },
                },
            });
            // Un solo segmento: el vertex shader no desplaza nada, así que los
            // 128×128 del original eran 16.000 vértices para no mover ninguno.
            const malla = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
            escena.add(malla);

            /* `offsetWidth/Height` y NO `getBoundingClientRect()`: el rect viene
               ya transformado, y esta foto vive dentro de un envoltorio que
               Academy escala de 1,18 a 1 al entrar. Si la escena termina de
               montarse a media animación —three son ~190 KB, y el salto por el
               ancla de «Academy» dura 1,3 s— el lienzo se dimensiona un 18 % de
               más y SE QUEDA así: el ResizeObserver vigila la caja de
               maquetación, y una transformación no la cambia, así que no hay
               segunda medida que lo corrija. Sin transformaciones las dos
               lecturas dan el mismo entero, que es lo que ya se calculaba. */
            const medir = () => {
                const w = Math.max(1, marco!.offsetWidth);
                const h = Math.max(1, marco!.offsetHeight);
                render.setSize(w, h, false);
                material.uniforms.uSize.value.set(w, h);
            };
            medir();
            const observador = new ResizeObserver(medir);
            observador.observe(marco!);

            const pintar = () => render.render(escena, camara);

            /* SALVAGUARDA: si la escena termina de montarse cuando la foto YA
               está en pantalla —scroll muy rápido, salto por ancla, red lenta—
               NO se lanza el barrido: se entra directamente al 100 %. Verías la
               fotografía desvanecerse y volver a formarse delante de ti, que es
               peor que no tener efecto. El revelado queda armado para la
               siguiente visita. */
            const arrancaLleno = yaSeVe();
            if (arrancaLleno) material.uniforms.uProgress.value = 1;
            pintar();
            setAlMando(true);

            /* El revelado se repite cada vez que la foto vuelve a entrar, igual
               que el resto de gestos de la página. El bucle de render solo corre
               MIENTRAS dura el barrido: quieto, la GPU no hace nada. */
            const tween = gsap.to(material.uniforms.uProgress, {
                value: 1,
                duration: duracion,
                ease: 'power2.out',
                paused: true,
                onUpdate: pintar,
                onComplete: pintar,
            });
            if (arrancaLleno) tween.progress(1).pause();

            // El observador dispara nada más crearse. Si ya entramos llenos,
            // esa primera llamada hay que dejarla pasar.
            let ignorarPrimera = arrancaLleno;
            const disparo = new IntersectionObserver(
                (entradas) => {
                    if (entradas[0].isIntersecting) {
                        if (ignorarPrimera) {
                            ignorarPrimera = false;
                            return;
                        }
                        tween.restart();
                        return;
                    }
                    // Al salir vuelve a cero para que el revelado se repita
                    // entero la próxima vez, igual que el resto de la página.
                    ignorarPrimera = false;
                    tween.pause(0);
                    pintar();
                },
                { threshold: 0.2 },
            );
            disparo.observe(marco!);

            soltar = () => {
                disparo.disconnect();
                observador.disconnect();
                tween.kill();
                malla.geometry.dispose();
                material.dispose();
                textura.dispose();
                render.dispose();
            };
        }

        return () => {
            vivo = false;
            vigia.disconnect();
            soltar?.();
        };
    }, [burbujas, duracion]);

    return (
        <div ref={marcoRef} className="relative">
            <img
                ref={imgRef}
                src={foto.src}
                srcSet={foto.srcSet}
                sizes={sizes}
                alt={t((foto.alt) as Parameters<Traducir>[0])}
                loading="lazy"
                decoding="async"
                style={{ objectPosition: foto.position }}
                className={`${className} transition-opacity duration-200 ${
                    alMando ? 'opacity-0' : 'opacity-100'
                }`}
            />
            {/* El lienzo solo existe visualmente cuando ya tiene la escena
                montada. Antes de eso es transparente y la <img> se ve intacta. */}
            <canvas
                ref={lienzoRef}
                aria-hidden="true"
                className={`absolute inset-0 h-full w-full ${alMando ? 'block' : 'hidden'}`}
            />
        </div>
    );
};

export default ReveladoFluido;
